import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  OrganizationTypeCreateSchema,
  OrganizationTypeUpdateSchema,
} from './schemas/organization-type-mutation-schema';
import type {
  OrganizationTypeQueryAllSchema,
  OrganizationTypeQuerySchema,
} from './schemas/organization-type-query-schema';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['ORGANIZATION_TYPES'] });
};

export const useGetOrganizationTypesMutation: UseControlledMutation<OrganizationTypeQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['ORGANIZATION_TYPES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<OrganizationTypeQueryAllSchema>({
            endpoint: '/api/v1/organization-types',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetOrganizationTypes(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['ORGANIZATION_TYPES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<OrganizationTypeQueryAllSchema>({
        endpoint: '/api/v1/organization-types',
        params: `${apiParamStr}&include=clientsCount`,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, OrganizationTypeCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/organization-types/',
        payload,
      }),
  });

export function useGetOrganizationType(id: string) {
  return useSuspenseQuery({
    queryKey: ['ORGANIZATION_TYPE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: OrganizationTypeQuerySchema }>({
        endpoint: `/api/v1/organization-types/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: OrganizationTypeQuerySchema }, ApiError, Partial<OrganizationTypeUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/organization-types/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['ORGANIZATION_TYPE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/organization-types/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
